import { useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
  message
} from 'antd';
import * as Icons from '@ant-design/icons';
import dayjs from 'dayjs';
import { withDevModalFilePath, SelfAssignButton, NumericInput, RichTextEditor } from '../common';

const { Modal } = withDevModalFilePath('client/src/components/WorkSchedule/EditModal.tsx');
import { useWorkOrderTemplates } from '../../hooks/useWorkOrderTemplates';
import { useMutateWorkSchedule } from '../../hooks/useWorkSchedules';
import { RecurrenceEditor } from './RecurrenceEditor';
import type { WorkScheduleDetail } from './DetailModal';
import type { RecurrenceRule } from '../../../../shared/types/work-schedule.types';
import { trpc } from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { useFormAutoBackup } from '../../hooks/useFormAutoBackup';

const { TextArea } = Input;
const { Title } = Typography;

const PRIORITY_OPTIONS = [
  { label: '低', value: 'low' },
  { label: '中', value: 'medium' },
  { label: '高', value: 'high' },
  { label: '緊急', value: 'urgent' }
];

const STATUS_OPTIONS = [
  { label: '予定', value: 'scheduled' },
  { label: '実行中', value: 'in_progress' },
  { label: '承認待ち', value: 'pending_approval' },
  { label: '完了', value: 'completed' },
  { label: 'キャンセル', value: 'cancelled' },
  { label: '延期', value: 'postponed' }
];

const RESULT_OPTIONS = [
  { label: '良好', value: 'good' },
  { label: '要注意', value: 'attention_needed' },
  { label: '修理必要', value: 'repair_required' }
];

const SCHEDULE_TYPE_OPTIONS = [
  { label: '定期', value: 'scheduled' },
  { label: '計画外', value: 'unscheduled' },
  { label: '緊急', value: 'emergency' }
];

type PriorityValue = (typeof PRIORITY_OPTIONS)[number]['value'];
type StatusValue = (typeof STATUS_OPTIONS)[number]['value'];
type ResultValue = (typeof RESULT_OPTIONS)[number]['value'];
type ScheduleTypeValue = (typeof SCHEDULE_TYPE_OPTIONS)[number]['value'];

interface EquipmentOptionSource {
  id: string;
  name?: string | null;
  code?: string | null;
}

const isEquipmentArray = (value: unknown): value is EquipmentOptionSource[] =>
  Array.isArray(value) &&
  value.every((equipment) => {
    if (!equipment || typeof equipment !== 'object') {
      return false;
    }
    const candidate = equipment as { id?: unknown };
    return typeof candidate.id === 'string';
  });

const extractEquipmentList = (source: unknown): EquipmentOptionSource[] => {
  if (isEquipmentArray(source)) {
    return source;
  }

  if (source && typeof source === 'object') {
    const collection = source as { items?: unknown; data?: unknown };
    if (isEquipmentArray(collection.items)) {
      return collection.items;
    }
    if (isEquipmentArray(collection.data)) {
      return collection.data;
    }
  }

  return [];
};

interface UserOptionSource {
  id: string;
  name?: string | null;
  email?: string | null;
}

const isUserOptionArray = (value: unknown): value is UserOptionSource[] =>
  Array.isArray(value) &&
  value.every((user) => {
    if (!user || typeof user !== 'object') {
      return false;
    }
    const candidate = user as { id?: unknown };
    return typeof candidate.id === 'string';
  });

const extractUserList = (source: unknown): UserOptionSource[] => {
  if (isUserOptionArray(source)) {
    return source;
  }

  if (source && typeof source === 'object') {
    const collection = source as { items?: unknown; data?: unknown };
    if (isUserOptionArray(collection.data)) {
      return collection.data;
    }
    if (isUserOptionArray(collection.items)) {
      return collection.items;
    }
  }

  return [];
};

interface ScheduleOptionSource {
  id: string;
  title?: string | null;
  displayId?: string | null;
}

const isScheduleOptionArray = (value: unknown): value is ScheduleOptionSource[] =>
  Array.isArray(value) &&
  value.every((schedule) => {
    if (!schedule || typeof schedule !== 'object') {
      return false;
    }
    const candidate = schedule as { id?: unknown };
    return typeof candidate.id === 'string';
  });

const extractScheduleList = (source: unknown): ScheduleOptionSource[] => {
  if (isScheduleOptionArray(source)) {
    return source;
  }

  if (source && typeof source === 'object') {
    const collection = source as { items?: unknown; data?: unknown; json?: unknown };
    if (isScheduleOptionArray(collection.items)) {
      return collection.items;
    }
    if (isScheduleOptionArray(collection.data)) {
      return collection.data;
    }
    if (isScheduleOptionArray(collection.json)) {
      return collection.json;
    }
  }

  return [];
};

export interface WorkScheduleEditModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  schedule?: WorkScheduleDetail | null;
  defaultDate?: Date | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormValues extends Record<string, unknown> {
  title: string;
  description?: string;
  equipmentId: string;
  workOrderTemplateId?: string | null;
  parentScheduleId?: string | null;
  scheduleType: ScheduleTypeValue;
  workTypeCategoryId?: string;
  priority: PriorityValue;
  status: StatusValue;
  scheduledDate: dayjs.Dayjs;
  scheduledDuration?: number;
  orderIndex?: number;
  recurrenceRule?: RecurrenceRule | null;
  assignedUserIds?: string[];
  workPerformed?: string;
  findings?: string;
  recommendations?: string;
  result?: ResultValue;
  requiresApproval?: boolean;
  approverUserIds?: string[];
}

export function WorkScheduleEditModal({
  open,
  mode,
  schedule,
  defaultDate,
  onClose,
  onSuccess
}: WorkScheduleEditModalProps): JSX.Element {
  const [form] = Form.useForm<FormValues>();
  const assignedUserIdsWatch = Form.useWatch('assignedUserIds', form) || [];
  const equipmentIdWatch = Form.useWatch('equipmentId', form);
  const workTypeCategoryIdWatch = Form.useWatch('workTypeCategoryId', form);
  const [messageApi, contextHolder] = message.useMessage();
  const { user } = useAuthStore();
  const effectiveCompanyId = schedule?.companyId ?? user?.companyId;

  // ユーザーがタイトルを手動入力したかどうかを追跡
  const userEditedTitleRef = useRef(false);

  // フォーム自動バックアップ（新規作成のみ有効）
  const { hasBackup, showRestoreConfirm, clearBackup } = useFormAutoBackup({    
    formType: 'work_schedule',
    entityId: undefined, // 新規作成のみなのでIDは不要
    form,
    enabled: open && mode === 'create',
  });

  const { createSchedule: createMutation, updateSchedule: updateMutation } = useMutateWorkSchedule();
  const { mutateAsync: createSchedule, isLoading: isCreating } = createMutation;
  const { mutateAsync: updateSchedule, isLoading: isUpdating } = updateMutation;

  const isSubmitting = isCreating || isUpdating;

  const { data: equipments, isLoading: loadingEquipments } =
    trpc.equipment.list.useQuery({
      page: 1,
      limit: 200
    }, {
      enabled: Boolean(effectiveCompanyId)
    });

  const { data: users, isLoading: loadingUsers } =
    trpc.user.list.useQuery({
      companyId: effectiveCompanyId,
      page: 1,
      limit: 200
    }, {
      enabled: Boolean(effectiveCompanyId)
    });

  const { templates } = useWorkOrderTemplates({ includeInactive: false });

  const { data: scheduleListData, isLoading: loadingScheduleList } =
    trpc.schedule.list.useQuery(
      { page: 1, limit: 100 },
      { enabled: open && Boolean(user?.companyId) }
    );
  const parentScheduleList = useMemo(() => {
    const list = extractScheduleList(scheduleListData);
    if (schedule?.id) {
      return list.filter((item) => item.id !== schedule.id);
    }
    return list;
  }, [scheduleListData, schedule?.id]);

  // 作業カテゴリを取得
  const { data: workTypeCategories = [], isLoading: isLoadingCategories } = trpc.workTypeCategory.list.useQuery(
    {
      includeInactive: false,
      sortBy: 'display_order',
      sortOrder: 'asc',
    },
    { enabled: open }
  );

  const equipmentList = useMemo(() => extractEquipmentList(equipments), [equipments]);

  const equipmentOptions = useMemo(
    () =>
      equipmentList.map((equipment) => ({
        label: equipment.name ?? equipment.code ?? equipment.id,
        value: equipment.id
      })),
    [equipmentList]
  );

  const userList = useMemo(() => extractUserList(users), [users]);

  const mergedUserList = useMemo(() => {
    const map = new Map<string, UserOptionSource>();
    for (const userItem of userList) {
      if (userItem?.id) {
        map.set(userItem.id, userItem);
      }
    }
    for (const userItem of schedule?.assignedUsers ?? []) {
      if (userItem?.id && !map.has(userItem.id)) {
        map.set(userItem.id, {
          id: userItem.id,
          name: userItem.name ?? null,
          email: userItem.email ?? null
        });
      }
    }
    if (schedule?.assignedTo && !map.has(schedule.assignedTo)) {
      map.set(schedule.assignedTo, { id: schedule.assignedTo });
    }
    return Array.from(map.values());
  }, [userList, schedule?.assignedUsers, schedule?.assignedTo]);

  const userOptions = useMemo(
    () =>
      mergedUserList.map((userItem) => ({
        label: userItem.name ?? userItem.email ?? userItem.id,
        value: userItem.id
      })),
    [mergedUserList]
  );

  // アイコンコンポーネントを取得するヘルパー関数
  const getIconComponent = (iconName?: string | null) => {
    if (!iconName) return null;
    const iconKey = iconName as keyof typeof Icons;
    const IconComponent = Icons[iconKey] as React.ComponentType<{ style?: React.CSSProperties }> | undefined;
    return IconComponent ? <IconComponent style={{ fontSize: 14 }} /> : null;
  };

  // 作業カテゴリのオプション

  const initialValues: Partial<FormValues> | undefined = useMemo(() => {
    if (!schedule) {
      return undefined;
    }
    return {
      title: schedule.title ?? '',
      description: schedule.description ?? undefined,
      equipmentId: schedule.selectedEquipments?.[0]?.equipmentId ?? undefined,
      workOrderTemplateId: schedule.workOrderTemplateId ?? undefined,
      parentScheduleId: schedule.parentScheduleId ?? undefined,
      scheduleType: schedule.scheduleType ?? 'scheduled',
      workTypeCategoryId: schedule.workTypeCategoryId ?? schedule.workTypeCategory?.id ?? undefined,
      priority: schedule.priority ?? 'medium',
      status: schedule.status ?? 'scheduled',
      scheduledDate: schedule.scheduledDate ? dayjs(schedule.scheduledDate) : dayjs(),
      scheduledDuration: schedule.scheduledDuration ?? undefined,
      orderIndex: schedule.orderIndex ?? undefined,
      recurrenceRule: (schedule.recurrenceRule ?? null) as RecurrenceRule | null,
      assignedUserIds: schedule.assignedUsers?.map((user) => user.id) ??
        (schedule.assignedTo ? [schedule.assignedTo] : undefined),
      workPerformed: schedule.workPerformed ?? undefined,
      findings: schedule.findings ?? undefined,
      recommendations: schedule.recommendations ?? undefined,
      result: schedule.result ?? undefined,
      requiresApproval: schedule.requiresApproval ?? false,
      approverUserIds: schedule.approverUserIds ?? undefined
    };
  }, [schedule]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (mode === 'edit' && initialValues) {
      form.setFieldsValue(initialValues as FormValues);
      // 編集モードではタイトルが既に設定されているとみなす
      userEditedTitleRef.current = true;
    } else {
      form.resetFields();
      form.setFieldsValue({
        scheduleType: 'scheduled',
        priority: 'medium',
        status: 'scheduled',
        scheduledDate: dayjs(defaultDate ?? new Date()),
        recurrenceRule: null
      });
      // 新規作成時はタイトル未編集状態にリセット
      userEditedTitleRef.current = false;
    }
  }, [open, mode, initialValues, form, defaultDate]);

  // 対象設備・作業カテゴリが選択されたときにタイトルを自動生成（新規作成時のみ）
  useEffect(() => {
    // 編集モードまたはユーザーが手動入力した場合はスキップ
    if (mode === 'edit' || userEditedTitleRef.current) {
      return;
    }

    // 両方が選択されている場合のみ自動生成
    if (equipmentIdWatch && workTypeCategoryIdWatch) {
      const equipment = equipmentList.find(e => e.id === equipmentIdWatch);
      const category = workTypeCategories.find(c => c.id === workTypeCategoryIdWatch);

      if (equipment && category) {
        const equipmentName = equipment.name ?? equipment.code ?? equipment.id;
        const autoTitle = `${category.categoryLabel} - ${equipmentName}`;
        form.setFieldValue('title', autoTitle);
      }
    }
  }, [mode, equipmentIdWatch, workTypeCategoryIdWatch, equipmentList, workTypeCategories, form]);

  // モーダルが開かれた時にバックアップの確認を表示
  useEffect(() => {
    if (open && hasBackup) {
      const timer = setTimeout(() => {
        showRestoreConfirm();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, hasBackup, showRestoreConfirm]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields() as FormValues;
      // workTypeCategoryId から workType を推論（後方互換性のため）
      const inferWorkType = (categoryId?: string): 'inspection' | 'repair' | 'replacement' | 'maintenance' => {
        const category = workTypeCategories.find(c => c.id === categoryId);
        if (!category) return 'maintenance';

        switch (category.categoryLabel) {
          case '定期点検': return 'inspection';
          case '緊急修理': return 'repair';
          case '部品・消耗品交換': return 'replacement';
          case '予防保全': return 'maintenance';
          default: return 'maintenance';
        }
      };

      const fallbackWorkType: 'inspection' | 'repair' | 'replacement' | 'maintenance' =
        schedule?.workType === 'inspection' ||
        schedule?.workType === 'repair' ||
        schedule?.workType === 'replacement' ||
        schedule?.workType === 'maintenance'
          ? schedule.workType
          : 'maintenance';

      type CreateScheduleInput = Exclude<Parameters<typeof createSchedule>[0], void>;

      const payload: CreateScheduleInput = {
        equipmentId: values.equipmentId,
        workOrderTemplateId: values.workOrderTemplateId ?? undefined,
        workTypeCategoryId: values.workTypeCategoryId ?? undefined,
        parentScheduleId: values.parentScheduleId ?? undefined,
        scheduleType: values.scheduleType as CreateScheduleInput['scheduleType'],
        workType: values.workTypeCategoryId ? inferWorkType(values.workTypeCategoryId) : fallbackWorkType,
        priority: values.priority as CreateScheduleInput['priority'],
        status: values.status as CreateScheduleInput['status'],
        title: values.title,
        description: values.description ?? undefined,
        scheduledDate: values.scheduledDate.toDate(),
        scheduledDuration: values.scheduledDuration ?? undefined,
        orderIndex: values.orderIndex ?? undefined,
        recurrenceRule: values.recurrenceRule ?? undefined,
        assignedUserIds: values.assignedUserIds ?? undefined,
        workPerformed: values.workPerformed ?? undefined,
        findings: values.findings ?? undefined,
        recommendations: values.recommendations ?? undefined,
        result: (values.result ?? undefined) as CreateScheduleInput['result'],
        requiresApproval: values.requiresApproval ?? undefined,
        approverUserIds: values.requiresApproval && values.approverUserIds && values.approverUserIds.length > 0
          ? values.approverUserIds
          : undefined,
        selectedEquipments: values.equipmentId
          ? [
            {
              equipmentId: values.equipmentId
            }
          ]
          : undefined
      };

      if (mode === 'create') {
        await createSchedule(payload);
        messageApi.success('スケジュールを作成しました');
        clearBackup(); // 保存成功時にバックアップをクリア
      } else if (schedule) {
        await updateSchedule({
          id: schedule.id,
          ...payload
        });
        messageApi.success('スケジュールを更新しました');
        clearBackup(); // 保存成功時にバックアップをクリア
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      if (error instanceof Error) {
        messageApi.error(error.message);
      }
      console.error(error);
    }
  };

  const footer = [
    <Button key="cancel" onClick={onClose} disabled={isSubmitting}>
      キャンセル
    </Button>,
    <Button
      key="submit"
      type="primary"
      onClick={handleSubmit}
      loading={isSubmitting}
    >
      {mode === 'create' ? '作成する' : '更新する'}
    </Button>
  ];

  return (
    <>
      {contextHolder}
      <Modal
        title={<Title level={4}>{mode === 'create' ? '新規スケジュール作成' : 'スケジュール編集'}</Title>}
        open={open}
        onCancel={onClose}
        okButtonProps={{ style: { display: 'none' } }}
        footer={footer}
        destroyOnHidden
        width={720}
      >
        <Form form={form} layout="vertical">
          {/* 対象設備・作業カテゴリを最上部に配置（タイトル自動生成のため） */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="対象設備"
                name="equipmentId"
                rules={[{ required: true, message: '対象設備を選択してください' }]}
              >
                <Select
                  loading={loadingEquipments}
                  placeholder="設備を選択"
                  options={equipmentOptions}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="作業カテゴリ"
                name="workTypeCategoryId"
                rules={[{ required: true, message: '作業カテゴリを選択してください' }]}
              >
                <Select
                  placeholder="作業カテゴリを選択"
                  allowClear
                  loading={isLoadingCategories}
                  optionRender={(option) => {
                    const category = workTypeCategories.find((c) => c.id === option.value);
                    if (!category) {
                      return option.label;
                    }
                    return (
                      <Space size={4}>
                        {getIconComponent(category.icon)}
                        {category.color && (
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              backgroundColor: category.color,
                              borderRadius: '2px',
                              border: '1px solid #d9d9d9',
                              display: 'inline-block'
                            }}
                          />
                        )}
                        {category.categoryLabel}
                      </Space>
                    );
                  }}
                >
                  {workTypeCategories.map((cat) => (
                    <Select.Option key={cat.id} value={cat.id}>
                      {cat.categoryLabel}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="タイトル"
            name="title"
            rules={[{ required: true, message: 'タイトルを入力してください' }]}
            extra={mode === 'create' && !userEditedTitleRef.current ? '対象設備と作業カテゴリを選択すると自動入力されます' : undefined}
          >
            <Input
              placeholder="例: 月次点検 - 第一工場"
              onChange={() => {
                // ユーザーが手動でタイトルを入力した場合、自動入力を無効化
                userEditedTitleRef.current = true;
              }}
            />
          </Form.Item>

          <Form.Item
            label="詳細"
            name="description"
            getValueFromEvent={(markdown: string) => markdown}
            getValueProps={(value) => ({ value: value || '' })}
          >
            <RichTextEditor
              placeholder="作業内容の概要を入力..."
              minHeight={100}
              showPreviewToggle={false}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="作業指示書" name="workOrderTemplateId">
                <Select
                  allowClear
                  placeholder="テンプレートを選択"
                  options={templates.map((template) => ({
                    label: `${template.templateName} (${template.displayId})`,
                    value: template.id
                  }))}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="担当者">
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="assignedUserIds" noStyle>
                    <Select
                      mode="multiple"
                      allowClear
                      loading={loadingUsers}
                      placeholder="担当者を選択（複数選択可）"
                      options={userOptions}
                      showSearch
                      optionFilterProp="label"
                      style={{ flex: 1 }}
                    />
                  </Form.Item>
                  <SelfAssignButton
                    currentValues={assignedUserIdsWatch}
                    onAssign={(userId) => {
                      if (!assignedUserIdsWatch.includes(userId)) {
                        form.setFieldValue('assignedUserIds', [...assignedUserIdsWatch, userId]);
                      }
                    }}
                  />
                </Space.Compact>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="親スケジュール" name="parentScheduleId">
                <Select
                  allowClear
                  placeholder="親スケジュールを選択"
                  loading={loadingScheduleList}
                  options={
                    parentScheduleList.map((item) => ({
                      label: item.title ?? item.displayId ?? item.id,
                      value: item.id
                    }))
                  }
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="スケジュール種別" name="scheduleType">
                <Select options={SCHEDULE_TYPE_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="優先度" name="priority">
            <Select options={PRIORITY_OPTIONS} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="ステータス" name="status">
                <Select options={STATUS_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="予定日時"
                name="scheduledDate"
                rules={[{ required: true, message: '予定日時を選択してください' }]}
              >
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="予定時間 (分)" name="scheduledDuration">
                <NumericInput min={15} step={15} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="繰り返し設定"
            shouldUpdate={(prev, current) => prev.scheduledDate !== current.scheduledDate}
          >
            {() => (
              <Form.Item name="recurrenceRule" valuePropName="value" noStyle>
                <RecurrenceEditor startDate={form.getFieldValue('scheduledDate')?.toDate?.()} />
              </Form.Item>
            )}
          </Form.Item>

          <Divider orientation="left">承認ワークフロー設定</Divider>

          <Form.Item name="requiresApproval" valuePropName="checked">
            <Checkbox>完了時に承認が必要</Checkbox>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, current) => prev.requiresApproval !== current.requiresApproval}
          >
            {() => {
              const requiresApproval = form.getFieldValue('requiresApproval');

              if (!requiresApproval) {
                return null;
              }

              return (
                <Form.Item label="承認者" name="approverUserIds">
                  <Select
                    mode="multiple"
                    placeholder="承認者を選択"
                    options={userOptions}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, current) => prev.status !== current.status}
          >
            {() => {
              const status = form.getFieldValue('status');
              const showWorkflowFields = status === 'in_progress' || status === 'pending_approval' || status === 'completed';

              if (!showWorkflowFields) {
                return null;
              }

              return (
                <>
                  <Alert
                    type="info"
                    message="作業報告"
                    description="作業内容、発見事項、推奨事項、作業結果を入力してください。"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  <Form.Item label="作業内容" name="workPerformed">
                    <TextArea
                      rows={4}
                      placeholder="実施した作業の内容を入力してください"
                    />
                  </Form.Item>

                  <Form.Item label="発見事項" name="findings">
                    <TextArea
                      rows={3}
                      placeholder="作業中に発見した問題や気になった点を入力してください（任意）"
                    />
                  </Form.Item>

                  <Form.Item label="推奨事項" name="recommendations">
                    <TextArea
                      rows={3}
                      placeholder="今後の対応や改善提案を入力してください（任意）"
                    />
                  </Form.Item>

                  <Form.Item label="作業結果" name="result">
                    <Select
                      allowClear
                      placeholder="作業結果を選択"
                      options={RESULT_OPTIONS}
                    />
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>

          {mode === 'edit' && schedule?.status === 'completed' && (
            <Alert
              type="warning"
              message="完了済みのスケジュールを更新する場合、履歴との整合性に注意してください。"
              showIcon
            />
          )}
        </Form>
      </Modal>
    </>
  );
}


